<template>
  <div
    class="sf-gallery flex-row overflow-hidden"
    :data-testid="isMounted && 'product-gallery--mounted'"
  >
    <LazyGalleryModal
      v-if="isGalleryModalOpen"
      :gallery-items="images"
      :gallery-video="videoDetails"
      :active-index="activeIndex"
      @change="openGalleryModal($event)"
      @close="closeGalleryModal"
    />
    <LazyThreeSixtyModal
      v-if="isThreeSixtyModalOpen"
      :three-sixty-image="threeSixtyImage"
      @close="closeThreeSixtyModal"
    />
    <PdpThumbnailCarousel
      class="desktop-only flex"
      :slider-items="images"
      :show-three-sixty="showThreeSixtyGalleryButton"
      :video-url="videoImageUrl"
      :video-alt="videoTitle"
      @item-click="({ index }) => openGalleryModal(index)"
      @three-sixty-click="openThreeSixtyModal()"
    />
    <div
      class="sf-gallery__stage relative"
    >
      <div class="overflow-hidden lg:my-3 w-full">
        <SimpleCarousel
          :slider-items="images"
          :has-custom-last-item="!!videoDetails"
          slide-class="relative w-full"
          :active-index="activeIndex"
          :draggable="false"
          :gallery-carousel="true"
          class="gallery-thumbs"
          @left="handleLeftTouch"
          @right="handleRightTouch"
          @item-click="({ index }) => openGalleryModal(index)"
        >
          <template #default="{ currentItem, index }">
            <TwicpicsPicture
              class="sf-gallery__big-image"
              :src="currentItem.desktop.url"
              :alt="currentItem.alt || 'mainImg'"
              :desktop-height="PRODUCT_GALLERY.IMAGE_HEIGHT_DESKTOP"
              :mobile-height="PRODUCT_GALLERY.IMAGE_HEIGHT_MOBILE"
              :preload="index === 0"
            />
          </template>
          <template
            #custom-last-item
            index="0"
          >
            <ImgView
              class="sf-gallery__big-image cursor-pointer"
              :src="videoDetails.desktop.url"
              :alt="videoDetails.alt"
              :itemprop="SCHEMA_ITEM_PROP.IMAGE"
            />
            <div
              class="video-overlay absolute inset-1/2 -mt-8 -ml-8 cursor-pointer"
            >
              <span class="icon iconify svg--video-play text-EXPONDO-primary-5 text-7xl" />
            </div>
          </template>
        </SimpleCarousel>
      </div>
      <span
        class="absolute left-0 top-0 pl-2 pt-1 text-EXPONDO-grey text-xs lg:hidden"
      >
        {{ $t('image-gallery-indicator', { slide: `${activeIndex + 1}`, totalSlides: totalSlidesCount }) }}
      </span>
      <button
        class="absolute bottom-0 right-0 iconify svg--enlarge text-EXPONDO-white mr-1 mb-1 p-2
        rounded-full bg-EXPONDO-primary-40 items-center cursor-pointer opacity-75 lg:hidden"
        :aria-label="$t('Make image fullscreen')"
        @click="openGalleryModal(activeIndex)"
      />
    </div>
    <div class="flex flex-col pt-2 items-center lg:hidden">
      <div class="w-full text-center mb-3">
        <PdpThumbnailCarouselMobile
          class="flex flow-row"
          :slider-items="images"
          @item-click="({ index }) => openGalleryModal(index)"
        />
      </div>
      <div>
        <button
          v-if="videoDetails"
          @click="openGalleryModal(images.length)"
        >
          <div
            class="border border-solid border-EXPONDO-primary-80 px-3 py-2 text-EXPONDO-primary-80 rounded-2xl font-bold
              flex items-center gap-2"
          >
            <span>{{ $t('video') }}</span>
            <span
              class="iconify svg--video-play-empty"
              data-testid="videoPlay"
            />
          </div>
        </button>
        <ClientOnly>
          <button
            v-if="showThreeSixtyGalleryButton"
            @click="openThreeSixtyModal()"
          >
            <div
              class="border border-solid border-EXPONDO-primary-80 px-3 py-2 text-EXPONDO-primary-80
              rounded-2xl font-bold flex items-center gap-2 ml-2"
            >
              <span>{{ $t('360') }}</span>
              <span
                class="iconify svg--360"
              />
            </div>
          </button>
        </ClientOnly>
      </div>
    </div>
  </div>
</template>
<script lang="ts">

import type { ProductVariant } from '~~/types/commercetools';
import { productGetters } from '~/utils/ct';

import { PRODUCT_GALLERY } from '~~/constants/productGallery';
import { ARROW_LEFT_KEY_CODE, ARROW_RIGHT_KEY_CODE } from '~~/constants/keyCodes';
import { SCHEMA_ITEM_PROP } from '~~/constants/seo';
import { SIMPLE_CAROUSEL_ARROW } from '~~/types/components/SimpleCarousel/SimpleCarouselArrows';
import { getProductVideo } from '~~/helpers/product/getProductVideo';
import { getProduct360Image } from '~~/helpers/product/getProduct360Image';
import type { ProductProcessedAttributes } from '~~/types/product/attribute/ProductProcessedAttributes';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import type { ProductDetail } from '~~/types/product/attribute/ProductDetail';

export default defineComponent({
  name: 'ProductGallery',

  props: {
    product: {
      type: Object as PropType<ProductVariant>,
      required: true,
    },
    attributesLocalized: {
      type: Object as PropType<ProductProcessedAttributes>,
      required: true,
    },
  },
  setup(props) {

    const i18n = useI18n();
    const { languageAndCountry } = useLocalization();
    const isGalleryModalOpen = ref<boolean>(false);
    const activeIndex = ref(0);
    const { isMounted } = useIsMounted();

    const handleGalleryPrevNextArrows = (e: KeyboardEvent) => {

      const isLeftOrRightArrowNotPressed = e.keyCode !== ARROW_LEFT_KEY_CODE && e.keyCode !== ARROW_RIGHT_KEY_CODE;

      if (isLeftOrRightArrowNotPressed || !isGalleryModalOpen.value) {

        return;

      }
      e.preventDefault();
      e.keyCode === ARROW_LEFT_KEY_CODE ? handleLeftTouch() : handleRightTouch();
    
    };

    onMounted(() => {

      window.addEventListener('keydown', handleGalleryPrevNextArrows);

    });
    onUnmounted(() => {

      window.removeEventListener('keydown', handleGalleryPrevNextArrows);
    
    });

    const handleLeftTouch = () => {

      if (activeIndex.value === totalSlidesCount.value - 1) {

        activeIndex.value = 0;
      
      } else {

        activeIndex.value += 1;
      
      }
    
    };

    const handleRightTouch = () => {

      if (activeIndex.value === minIndex) {

        activeIndex.value = totalSlidesCount.value - 1;
      
      } else {

        activeIndex.value -= 1;
      
      }
    
    };

    const openGalleryModal = (index: number) => {

      activeIndex.value = index;
      isGalleryModalOpen.value = true;
    
    };

    const closeGalleryModal = () => {

      isGalleryModalOpen.value = false;
    
    };

    const technicalDetails = computed<ProductDetail[]>(() => props.attributesLocalized?.techdetails || []);

    const getImageAlt = (product: ProductVariantExtended, index: number): string => {

      const productName = product._name || 'product';

      if (index === 0) {

        return productName;
      
      }

      const technicalDetail = technicalDetails.value[index - 1];
      const technicalDetailKey = technicalDetail?.key;
      const technicalDetailValue = technicalDetail?.value || technicalDetail?.values?.[0];

      if (!technicalDetailKey || !technicalDetailValue) {

        return `${productName} (${index})`;
      
      }

      const sufix = ` (${technicalDetail.key}: ${technicalDetail.value || technicalDetail.values[0]})`;

      return `${productName}${sufix}`;
    
    };

    const images = computed(() => productGetters.getGallery(props.product).map((img, index) => ({
      mobile: { url: img.small },
      desktop: { url: img.normal },
      big: { url: img.big },
      alt: getImageAlt(props.product, index),
      originalIndex: index,
    })));

    const videoInformation = computed(() => getProductVideo(props.product, languageAndCountry.value));

    const totalSlidesCount = computed(() => videoDetails.value ? images.value.length + 1 : images.value.length);

    const getImageBulletClass = (index: number, activeIndex: number): string => {

      return index === activeIndex ? 'iconify svg--circle-full text-EXPONDO-primary-80' : 'iconify svg--circle-empty';
    
    };

    const onClickMoreImages = () => {

      isGalleryModalOpen.value = true;
    
    };

    const productTitle = computed(() => productGetters.getName(props.product));
    const videoDetails = computed(() => {

      if (!videoInformation.value) {

        return null;
      
      }
      const videoLink = videoInformation.value.link;
      const videoThumbnailLink = videoInformation.value.thumbnail;

      return {
        mobile: {
          url: videoThumbnailLink,
        },
        desktop: {
          url: videoThumbnailLink,
        },
        big: {
          url: videoThumbnailLink,
        },
        play: {
          url: videoLink,
        },
        alt: `${i18n.t('video')} - ${productTitle.value}`,
      };
    
    });

    const minIndex = 0;
    const onActiveImgChange = (index: number) => {

      if (index < minIndex || index > totalSlidesCount.value) {

        return;
      
      }
      openGalleryModal(index);
    
    };
    const desktopArrowClass = {
      container: 'h-12 cursor-pointer absolute z-1 top-carouselControl flex items-center',
      arrow: 'text-EXPONDO-black text-2xl',
    };

    const threeSixtyImage = computed(() => getProduct360Image(props.product));
    const showThreeSixtyGalleryButton = computed(() => {

      return !!threeSixtyImage.value;
    
    });
    const productSku = computed(() => props.product.sku);
    const isThreeSixtyModalOpen = ref<boolean>(false);

    const openThreeSixtyModal = () => {

      isThreeSixtyModalOpen.value = true;
    
    };

    const closeThreeSixtyModal = () => {

      isThreeSixtyModalOpen.value = false;
    
    };

    const videoImageUrl = computed(() =>
      (videoDetails.value && videoDetails.value.mobile && videoDetails.value.mobile.url) || '',
    );

    const videoTitle = computed(() => (videoDetails.value && videoDetails.value.alt) || '');

    return {
      isGalleryModalOpen,
      openGalleryModal,
      closeGalleryModal,
      onClickMoreImages,
      videoDetails,
      activeIndex,
      PRODUCT_GALLERY,
      totalSlidesCount,
      getImageBulletClass,
      onActiveImgChange,
      minIndex,
      handleLeftTouch,
      handleRightTouch,
      desktopArrowClass,
      images,
      SCHEMA_ITEM_PROP,
      isMounted,
      SIMPLE_CAROUSEL_ARROW,
      threeSixtyImage,
      showThreeSixtyGalleryButton,
      isThreeSixtyModalOpen,
      openThreeSixtyModal,
      closeThreeSixtyModal,
      productSku,
      videoImageUrl,
      videoTitle,
    };
  
  },
});


</script>
<style lang="scss">
@use "~/assets/scss/helpers/_breakpoints" as breakpoints;
@use "~/assets/scss/sf-gallery" as sf-gallery;
$gallery-image-placeholder-mobile-height: 250px;
$gallery-image-placeholder-desktop-height: 500px;

.product {
  .sf-gallery {
    --gallery-thumbs-flex-direction: row;
    --gallery-thumbs-order: 0;
    --gallery-thumbs-flex: auto;

    &__stage {
      max-width: 100%;

      @include breakpoints.for-desktop {
        margin-bottom: var(--spacer-base);
        border-radius: var(--buttons-border-radius);
      }

      .sf-gallery__big-image {
        width: 100%;
        height: $gallery-image-placeholder-mobile-height;

        @include breakpoints.for-desktop {
          height: $gallery-image-placeholder-desktop-height;
        }
      }
    }
  }
}

.video-overlay {
  position: absolute;
}
</style>
